import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Star } from 'lucide-react';
import { getBallot, saveBallot, submitBallotApi, type CandidateHappening } from '@/lib/selections-api';
import { playableSource, RowPlay, PlayerBar } from './BallotPlayer';
import type { BallotEntryInput, NominateRoundConfig } from '@littlebooker/dal';

interface Entry {
  is_wildcard: boolean;
  note: string;
}

interface NominateBallotProps {
  selectionId: string;
  roundId: string;
  candidates: CandidateHappening[];
  recusedIds: Set<string>;
  config: NominateRoundConfig;
  editable: boolean;
}

const noteClass = 'w-full rounded-md border border-input bg-background px-2 py-1 text-sm';

export function NominateBallot({
  selectionId,
  roundId,
  candidates,
  recusedIds,
  config,
  editable,
}: NominateBallotProps) {
  const queryClient = useQueryClient();
  const maxPicks = config.max_picks ?? 0;
  const wildcardAllowance = config.wildcards ?? 0;
  const requireNote = !!config.require_note;

  const { data: ballot, isLoading } = useQuery({
    queryKey: ['selection-ballot', roundId],
    queryFn: () => getBallot(selectionId, roundId),
  });

  // happening_id -> Entry (present = nominated)
  const [picks, setPicks] = useState<Record<string, Entry>>({});
  const [error, setError] = useState('');
  const [nowPlaying, setNowPlaying] = useState<{ id: string; url: string; title: string } | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const initRef = useRef(false);

  // Hydrate from the saved ballot once loaded.
  useEffect(() => {
    if (!ballot) return;
    const next: Record<string, Entry> = {};
    for (const e of ballot.entries) {
      next[e.happening_id] = { is_wildcard: e.is_wildcard, note: e.note ?? '' };
    }
    setPicks(next);
  }, [ballot?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start collapsed if the ballot is already submitted (only on first load, so
  // reopening to view/edit isn't fought by refetches).
  useEffect(() => {
    if (!initRef.current && ballot) {
      initRef.current = true;
      if (ballot.status === 'submitted') setCollapsed(true);
    }
  }, [ballot]);

  const toBallotEntries = (): BallotEntryInput[] =>
    Object.entries(picks).map(([happening_id, v]) => ({
      happening_id,
      points: null,
      is_wildcard: v.is_wildcard,
      note: v.note.trim() || null,
    }));

  const saveMutation = useMutation({
    mutationFn: () => saveBallot(selectionId, roundId, toBallotEntries()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['selection-ballot', roundId] }),
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Failed to save'),
  });
  const submitMutation = useMutation({
    mutationFn: () => submitBallotApi(selectionId, roundId, toBallotEntries()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['selection-ballot', roundId] });
      setCollapsed(true); // collapse the list once submitted
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Failed to submit'),
  });

  const entries = Object.entries(picks);
  const regularCount = entries.filter(([, v]) => !v.is_wildcard).length;
  const wildcardCount = entries.filter(([, v]) => v.is_wildcard).length;
  const missingNote = requireNote && entries.some(([, v]) => !v.note.trim());

  const overPicks = regularCount > maxPicks;
  const overWildcards = wildcardCount > wildcardAllowance;
  const canSubmit = editable && !overPicks && !overWildcards && !missingNote && entries.length > 0;

  const toggle = (id: string) =>
    setPicks((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = { is_wildcard: false, note: '' };
      return next;
    });
  const setWildcard = (id: string, on: boolean) =>
    setPicks((prev) => ({ ...prev, [id]: { ...prev[id], is_wildcard: on } }));
  const setNote = (id: string, note: string) =>
    setPicks((prev) => ({ ...prev, [id]: { ...prev[id], note } }));

  const submitted = ballot?.status === 'submitted';

  const counterClass = (over: boolean) =>
    over ? 'font-medium text-destructive' : 'font-medium text-foreground';

  const sorted = useMemo(
    () =>
      [...candidates].sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? '')),
    [candidates],
  );

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading ballot…</p>;

  if (collapsed) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/20 p-3 text-sm">
        <span className="font-medium text-primary">✓ Ballot submitted</span>
        <span className="text-muted-foreground">
          {regularCount} pick{regularCount === 1 ? '' : 's'}
          {wildcardCount > 0 ? ` · ${wildcardCount} wildcard${wildcardCount === 1 ? '' : 's'}` : ''}
        </span>
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => setCollapsed(false)}>
          View / edit
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span>
          Picks <span className={counterClass(overPicks)}>{regularCount}</span> / {maxPicks}
        </span>
        <span>
          Wildcards <span className={counterClass(overWildcards)}>{wildcardCount}</span> /{' '}
          {wildcardAllowance}
        </span>
        <span className="text-muted-foreground">
          {submitted ? 'Submitted' : 'Draft'}
          {!editable && ' · round not open'}
        </span>
      </div>

      <p className="text-xs text-muted-foreground">
        Wildcards are a separate allowance — a wildcard nomination is guaranteed onto the shortlist
        even if nobody else picks it.
      </p>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">Pick</TableHead>
              <TableHead>Show</TableHead>
              <TableHead>Artists</TableHead>
              <TableHead className="w-16">Wild</TableHead>
              <TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((c) => {
              const picked = !!picks[c.id];
              const recused = recusedIds.has(c.id);
              return (
                <Fragment key={c.id}>
                <TableRow className={recused ? 'opacity-50' : ''}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={picked}
                      disabled={!editable || recused}
                      onChange={() => toggle(c.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <RowPlay
                        source={playableSource(c.archive_links)}
                        onPlay={() => {
                          const s = playableSource(c.archive_links);
                          if (s) setNowPlaying({ id: c.id, url: s.url, title: c.title });
                        }}
                      />
                      {c.episode_number != null && (
                        <span className="text-muted-foreground">#{c.episode_number}</span>
                      )}
                      <span>{c.title}</span>
                      {recused && <span className="text-xs text-muted-foreground">(recused)</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {c.artists.map((a) => a.name).join(', ') || '—'}
                  </TableCell>
                  <TableCell>
                    {picked && (
                      <button
                        type="button"
                        title="Wildcard"
                        disabled={!editable}
                        onClick={() => setWildcard(c.id, !picks[c.id].is_wildcard)}
                      >
                        <Star
                          className={
                            picks[c.id].is_wildcard
                              ? 'h-4 w-4 fill-current text-primary'
                              : 'h-4 w-4 text-muted-foreground'
                          }
                        />
                      </button>
                    )}
                  </TableCell>
                  <TableCell>
                    {picked && (
                      <input
                        className={noteClass}
                        placeholder={requireNote ? 'Note required' : 'Optional note'}
                        value={picks[c.id].note}
                        disabled={!editable}
                        onChange={(e) => setNote(c.id, e.target.value)}
                      />
                    )}
                  </TableCell>
                </TableRow>
                {nowPlaying?.id === c.id && (
                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                    <TableCell colSpan={5}>
                      <PlayerBar track={nowPlaying} onClose={() => setNowPlaying(null)} />
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {(overPicks || overWildcards || missingNote) && (
        <p className="text-sm text-destructive">
          {overPicks && `Too many picks (max ${maxPicks}). `}
          {overWildcards && `Too many wildcards (max ${wildcardAllowance}). `}
          {missingNote && 'Every nomination needs a note. '}
        </p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {editable && !submitted && (
        <p className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
          ⚠ Not submitted yet — “Save draft” only saves your progress. Click{' '}
          <span className="font-medium">Submit</span> to actually cast your vote.
        </p>
      )}

      {editable && (
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setError('');
              saveMutation.mutate();
            }}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save draft'}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setError('');
              submitMutation.mutate();
            }}
            disabled={!canSubmit || submitMutation.isPending}
          >
            {submitMutation.isPending ? 'Submitting…' : submitted ? 'Re-submit' : 'Submit'}
          </Button>
        </div>
      )}
    </div>
  );
}
